import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Box, Grid, Typography } from "@mui/material";
import { format } from "date-fns";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../../app/hooks";
import { HoverPopper } from "../../../common/components/functional/hoverPopper/HoverPopper";
import { dateTimeFormat } from "../../../common/helpers/formatDateTime";
import isEmpty from "../../../common/helpers/isEmpty";
import { CoreUserType } from "../../user/types/entities";
import {
  ApprovalStateChange,
  EditEntry,
  Entry,
  EntryChange,
} from "../entriesAPI";
import { HistoryType, ParsedApprovalChange, ParsedEntryChange } from "../types";
import { EntryChangeHistoryPopper } from "./EntryChangeHistoryPopper";
import {
  parseApprovalChanges,
  parseEntryChanges,
} from "./Shared/entryChangeHistoryUtils";

type Props = {
  entry?: Entry | EditEntry | null;
};

const EntryChanges = ({ entry }: Props) => {
  const { t } = useTranslation();
  const { user } = useAppSelector((state) => state.user);
  const { entryType } = useAppSelector((state) => state.localized);

  const isApproved = entry?.approvalState === "Approved";
  const isRejected =
    entry?.approvalState === "CustomerRejected" ||
    entry?.approvalState === "Rejected";

  const formatTimestamp = (timestamp: number): string => {
    return format(new Date(timestamp), dateTimeFormat);
  };

  const getUserName = (change?: EntryChange | ApprovalStateChange): string => {
    if (change) {
      if (
        change.userType === CoreUserType.EXPERT ||
        change.userType === CoreUserType.CUSTOMER ||
        change.userType === "Talent"
      ) {
        const translatedUserType = t(`common:${change.userType}`);
        return change.userFirstName && change.userLastName
          ? `${change.userLastName}, ${change.userFirstName} (${translatedUserType})`
          : `${t("payslips:unknown")} (${translatedUserType})`;
      }
      if (change.userType === "System" || change.userId === "system-user") {
        return t("common:System");
      }
      if (change.userId === "worktime-integration") {
        return t("common:WorktimeIntegration");
      }
    }

    // existing entry which is not backfilled with creation change
    if (entry?.id) {
      return t("hourEntries:notKnown");
    }

    // new entry
    return `${user?.lastName}, ${user?.firstName} (Expert)`;
  };

  const renderDateTimeAndUser = (
    timestamp: number,
    change?: EntryChange | ApprovalStateChange,
  ): string => {
    return `${formatTimestamp(timestamp)} ${getUserName(change)}`;
  };

  const sortedEntryChanges: EntryChange[] =
    entry && entry.entryChanges
      ? [...entry.entryChanges].sort((a, b) => a.timestamp - b.timestamp)
      : [];

  // existing entry with no created change
  // or modified entry, where the timestamp of the first change differs from entry.created
  const isLegacyEntry: boolean =
    (entry?.entryChanges.length === 0 && !!entry?.id && !!entry.created) ||
    (!!sortedEntryChanges.length &&
      sortedEntryChanges[0].timestamp !== entry?.created);

  const createdEntryChange = isLegacyEntry
    ? // legacy entries does not have created change
      undefined
    : // in newer entries creation generates the first change
      sortedEntryChanges[0];

  const createdTimestamp =
    createdEntryChange?.timestamp || entry?.created || Date.now();

  const getModifiedEntryChange = () => {
    if (isLegacyEntry) {
      // not shown if undefined
      return !!sortedEntryChanges.length && sortedEntryChanges.length <= 1
        ? // show the only change as modification in case of legacy entry
          sortedEntryChanges[0]
        : // if there are more changes, show the last one as modification
          sortedEntryChanges[sortedEntryChanges.length - 1];
    }

    return sortedEntryChanges.length > 1
      ? sortedEntryChanges[sortedEntryChanges.length - 1]
      : // ignore the first change, as it is the creation change
        undefined;
  };

  const modifiedEntryChange = getModifiedEntryChange();

  const sortedApprovalChanges: ApprovalStateChange[] =
    entry && entry.approvalStateChanges
      ? [...entry.approvalStateChanges].sort(
          (a, b) => a.timestamp - b.timestamp,
        )
      : [];

  const getApprovalChange = (state: "Approved" | "Rejected") => {
    const filtered = sortedApprovalChanges.filter((change) => {
      if (state === "Approved") {
        return change.approvalState === "Approved";
      } else {
        return (
          change.approvalState === "Rejected" ||
          change.approvalState === "CustomerRejected"
        );
      }
    });
    return filtered.length > 0 ? filtered[filtered.length - 1] : undefined;
  };

  const approvedChange = isApproved ? getApprovalChange("Approved") : undefined;
  const rejectedChange = isRejected ? getApprovalChange("Rejected") : undefined;

  const entryId = entry?.id || "-";

  const changeHistory: ParsedEntryChange[] = useMemo(() => {
    if (!entry?.entryChanges) return [];
    return parseEntryChanges(entry.entryChanges, t, entryType);
    // FIXME: fix this or add comment why this is needed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry?.entryChanges, t]);

  const hasChangeHistory = !isEmpty(changeHistory);

  const approvalHistory: ParsedApprovalChange[] = useMemo(() => {
    if (!entry?.approvalStateChanges) return [];
    return parseApprovalChanges(entry.approvalStateChanges);
  }, [entry?.approvalStateChanges]);

  const hasApprovalHistory = !isEmpty(approvalHistory);

  return (
    <Box paddingLeft="8px">
      <Grid container width="600px" spacing={0}>
        <Grid item container xs={2} direction="column" spacing={0}>
          <Grid item>
            <Typography fontSize="12px" variant="secondary">
              {`${t("hourEntries:created")}:`}
            </Typography>
          </Grid>
          {modifiedEntryChange && (
            <Grid item>
              <Typography fontSize="12px" variant="secondary">
                {`${t("hourEntries:modified")}:`}
              </Typography>
            </Grid>
          )}
          {isApproved && (
            <Grid item>
              <Typography fontSize="12px" variant="secondary" fontWeight={700}>
                {`${t("hourEntries:Approved")}:`}
              </Typography>
            </Grid>
          )}
          {isRejected && (
            <Grid item>
              <Typography fontSize="12px" variant="secondary" fontWeight={700}>
                {`${t("hourEntries:CustomerRejected")}:`}
              </Typography>
            </Grid>
          )}
          <Grid item>
            <Typography fontSize="12px" variant="secondary">
              {`${t("hourEntries:entryId")}:`}
            </Typography>
          </Grid>
        </Grid>
        <Grid item container xs={10} direction="column" spacing={0}>
          <Grid item>
            <Typography fontSize="12px" variant="secondary" fontWeight={600}>
              {renderDateTimeAndUser(createdTimestamp, createdEntryChange)}
            </Typography>
          </Grid>
          {modifiedEntryChange && (
            <Grid item>
              <Typography
                fontSize="12px"
                variant="secondary"
                fontWeight={600}
                component="span"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                {renderDateTimeAndUser(
                  modifiedEntryChange.timestamp,
                  modifiedEntryChange,
                )}

                {hasChangeHistory && (
                  <HoverPopper
                    trigger={
                      <InfoOutlinedIcon
                        sx={{
                          fontSize: "16px",
                          color: "primary.main",
                          cursor: "pointer",
                          display: "inline-block",
                          verticalAlign: "middle",
                        }}
                      />
                    }
                    placement="bottom-start"
                    maxWidth="800px"
                  >
                    <EntryChangeHistoryPopper
                      type={HistoryType.FieldChanges}
                      changes={changeHistory}
                      getUserName={getUserName}
                    />
                  </HoverPopper>
                )}
              </Typography>
            </Grid>
          )}
          {isApproved && approvedChange && (
            <Grid item>
              <Typography
                fontSize="12px"
                variant="secondary"
                fontWeight={600}
                component="span"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                {renderDateTimeAndUser(
                  approvedChange.timestamp,
                  approvedChange,
                )}

                {hasApprovalHistory && (
                  <HoverPopper
                    trigger={
                      <InfoOutlinedIcon
                        sx={{
                          fontSize: "16px",
                          color: "primary.main",
                          cursor: "pointer",
                          display: "inline-block",
                          verticalAlign: "middle",
                        }}
                      />
                    }
                    placement="bottom-start"
                    maxWidth="800px"
                  >
                    <EntryChangeHistoryPopper
                      type={HistoryType.ApprovalChanges}
                      changes={approvalHistory}
                      getUserName={getUserName}
                    />
                  </HoverPopper>
                )}
              </Typography>
            </Grid>
          )}
          {isRejected && rejectedChange && (
            <Grid item>
              <Typography
                fontSize="12px"
                variant="secondary"
                fontWeight={600}
                component="span"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                {renderDateTimeAndUser(
                  rejectedChange.timestamp,
                  rejectedChange,
                )}

                {hasApprovalHistory && (
                  <HoverPopper
                    trigger={
                      <InfoOutlinedIcon
                        sx={{
                          fontSize: "16px",
                          color: "primary.main",
                          cursor: "pointer",
                          display: "inline-block",
                          verticalAlign: "middle",
                        }}
                      />
                    }
                    placement="bottom-start"
                    maxWidth="800px"
                  >
                    <EntryChangeHistoryPopper
                      type={HistoryType.ApprovalChanges}
                      changes={approvalHistory}
                      getUserName={getUserName}
                    />
                  </HoverPopper>
                )}
              </Typography>
            </Grid>
          )}
          <Grid item>
            <Typography fontSize="12px" variant="secondary" fontWeight={600}>
              {entryId}
            </Typography>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

export default EntryChanges;
